import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Country, State, City } from 'country-state-city';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import InstitutionStepper from '../components/stepper/InstitutionStepper';
import { toast } from 'react-hot-toast';

// By default, we'll show only India. To show all countries, set this to false
const INDIA_ONLY = true;

function InstituteDetails() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [institutionDetails, setInstitutionDetails] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    institutionName: '',
    websiteLink: '',
    educationBoard: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: INDIA_ONLY ? 'IN' : '',
  });

  // States for location data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Initialize countries
    const allCountries = Country.getAllCountries();
    const filteredCountries = INDIA_ONLY 
      ? allCountries.filter(country => country.isoCode === 'IN')
      : allCountries;
    setCountries(filteredCountries);

    // If INDIA_ONLY is true, initialize states for India
    if (INDIA_ONLY) {
      const indianStates = State.getStatesOfCountry('IN');
      setStates(indianStates);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchInstitutionDetails();
    }
  }, [user]);

  useEffect(() => {
    // Update states when country changes
    if (formData.country) {
      const countryStates = State.getStatesOfCountry(formData.country);
      setStates(countryStates);
      // Clear state and city when country changes
      if (formData.state) {
        setFormData(prev => ({ ...prev, state: '', city: '' }));
      }
    }
  }, [formData.country]);

  useEffect(() => {
    // Update cities when state changes
    if (formData.country && formData.state) {
      const stateCities = City.getCitiesOfState(formData.country, formData.state);
      setCities(stateCities);
      // Clear city when state changes
      if (formData.city) {
        setFormData(prev => ({ ...prev, city: '' }));
      }
    }
  }, [formData.country, formData.state]);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(data);

      // Redirect if user is not an institution
      if (data.user_type !== 'institution') {
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error loading profile');
    }
  };

  const fetchInstitutionDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('institution_details')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setInstitutionDetails(data);
        setBannerPreview(data.banner_image_url);
        setFormData({
          institutionName: data.institution_name || '',
          websiteLink: data.website_link || '',
          educationBoard: data.education_board || '',
          street: data.street || '',
          city: data.city || '',
          state: data.state || '',
          postalCode: data.postal_code || '',
          country: data.country || (INDIA_ONLY ? 'IN' : ''),
        });
      }
    } catch (error) {
      console.error('Error fetching institution details:', error);
      toast.error('Error loading institution details');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBannerDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleBannerFile(file);
  };

  const handleBannerFile = (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should not exceed 5MB');
      return;
    }

    setBannerFile(file);
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleBannerRemove = async () => {
    try {
      if (institutionDetails?.banner_image_url) {
       
        const fileName = institutionDetails.banner_image_url.split('/').pop();
        const filePath = `${user.id}/${fileName}`;
        const { error: storageError } = await supabase.storage
          .from('institution_banners')
          .remove([filePath]);
  
        if (storageError) throw storageError;
  
        const { error: dbError } = await supabase
          .from('institution_details')
          .update({ banner_image_url: null })
          .eq('user_id', user.id);
  
        if (dbError) throw dbError;
  
        setInstitutionDetails(prev => ({ ...prev, banner_image_url: null }));
      }
  
      setBannerFile(null);
      setBannerPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
  
      toast.success('Institute Image removed successfully');
    } catch (error) {
      console.error('Error removing Image:', error);
      toast.error('Failed to remove Image');
    }
  };

  const uploadBanner = async () => {
    if (!bannerFile) return null;

    const fileExt = bannerFile.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    try {
      // Delete old banner if exists
      if (institutionDetails?.banner_image_url) {
        const oldPath = institutionDetails.banner_image_url.split('/').pop();
        await supabase.storage
          .from('institution_banners')
          .remove([`${user.id}/${oldPath}`]);
      }

      // Upload new banner
      const { error: uploadError } = await supabase.storage
        .from('institution_banners')
        .upload(fileName, bannerFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('institution_banners')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading banner:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let bannerUrl = institutionDetails?.banner_image_url;

      // Upload banner if new file is selected
      if (bannerFile) {
        bannerUrl = await uploadBanner();
      }

      const { data: existingData, error: fetchError } = await supabase
        .from('institution_details')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const institutionData = {
        user_id: user.id,
        institution_name: formData.institutionName,
        website_link: formData.websiteLink,
        education_board: formData.educationBoard,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country,
        banner_image_url: bannerUrl
      };

      let error;
      if (existingData) {
        // Update existing record
        ({ error } = await supabase
          .from('institution_details')
          .update(institutionData)
          .eq('id', existingData.id));
      } else {
        // Insert new record
        ({ error } = await supabase
          .from('institution_details')
          .insert([institutionData]));
      }

      if (error) throw error;

      toast.success('Institution details saved successfully!');
      navigate('/vacancy-details');
    } catch (error) {
      console.error('Error saving institution details:', error);
      toast.error('Failed to save data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-primary-50">
      <Navigation />
      <div className="flex-grow pt-16 md:pt-20 mt-2 md:mt-5 mb-4">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          {/* Mobile Stepper */}
          <div className="block md:hidden mb-6">
            <InstitutionStepper />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
            {/* Desktop Stepper */}
            <div className="hidden md:block md:col-span-3">
              <div className="sticky top-24">
                <InstitutionStepper />
              </div>
            </div>

            {/* Form Section */}
            <div className="col-span-1 md:col-span-9">
              <div className="bg-primary-extra rounded-lg shadow-sm p-4 md:p-8">
                <h1 className="text-xl md:text-2xl font-bold text-primary-500 mb-6 md:mb-8">Institution Details</h1>

                {/* Profile Section */}
                <div className="bg-primary-50 rounded-lg p-6 mb-8">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div>
                      <h2 className="text-xl font-semibold text-primary-text">
                        {formData.institutionName || 'Institution Name Not Specified'}
                      </h2>
                      <p className="text-primary-footerText mt-1">{user.email}</p>
                      <p className="text-primary-footerText mt-1">
                        {formData.city ? (
                          <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {formData.city}
                          </span>
                        ) : 'Location Not Specified'}
                      </p>
                    </div>
                  </div>

                  {/* Banner Image Section */}
                  <div className="mt-6">
                    <div
                      className="relative border-2 border-dashed border-primary-200 rounded-lg p-4 h-48"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleBannerDrop}
                    >
                      {bannerPreview ? (
                        <div className="relative h-full">
                          <img
                            src={bannerPreview}
                            alt="Institution Image"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <div className="absolute top-2 right-2 flex space-x-2">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-500" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={handleBannerRemove}
                              className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="flex flex-col items-center justify-center h-full cursor-pointer"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-primary-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="mt-2 text-sm text-primary-400">
                            Drop your institute image here or <span className="text-primary-500">browse</span>
                          </p>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleBannerFile(e.target.files?.[0])}
                      />
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Institution Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="institutionName"
                        value={formData.institutionName}
                        onChange={handleInputChange}
                        placeholder="Enter institution name"
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Website Link <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="websiteLink"
                        value={formData.websiteLink}
                        onChange={handleInputChange}
                        placeholder="www.institution.com"
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Education Board <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="educationBoard"
                        value={formData.educationBoard}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      >
                        <option value="">Please Select</option>
                        <option value="cbse">CBSE</option>
                        <option value="icse">ICSE</option>
                        <option value="state">State Board</option>
                        <option value="ib">IB</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div className="col-span-full">
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Street <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="street"
                        value={formData.street}
                        onChange={handleInputChange}
                        placeholder="Add Address"
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country.isoCode} value={country.isoCode}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        State <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                        disabled={!formData.country}
                      >
                        <option value="">Select State</option>
                        {states.map(state => (
                          <option key={state.isoCode} value={state.isoCode}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        City <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                        disabled={!formData.state}
                      >
                        <option value="">Select City</option>
                        {cities.map(city => (
                          <option key={city.name} value={city.name}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-primary-text mb-1">
                        Postal Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="Enter Postal code"
                        className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="px-6 py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2.5 bg-primary-500 text-primary-50 rounded-md hover:bg-opacity-90 text-sm disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save & Next'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default InstituteDetails;